import { BusinessOrgs } from '@/enums'
import { fromEventTarget, splitAtLastIndex } from '@/utils'
import { join, values as rValues, trim } from 'ramda'
import { Option, Select } from '../../atoms'
import { GroupInputContainer, Input } from '../../styled'
import { BasicFormItem } from '../FormItem'

// Define the type for BusinessOrgs values
type BusinessOrgsType = typeof BusinessOrgs[keyof typeof BusinessOrgs]

const businessOrgs = rValues(BusinessOrgs) as BusinessOrgsType[]

const makeInputValue = (companyName = ''): [string, BusinessOrgsType?] => {
  const [init, last] = splitAtLastIndex(' ', companyName)

  if (businessOrgs.includes(last as BusinessOrgsType))
    return [init, last as BusinessOrgsType]

  if (last) return [join(' ', [init, last])]

  return [init]
}

export interface CompanyNameProps {
  value?: string
  onChange: (value: string) => void
  disabled?: boolean
  error?: boolean
}

export const CompanyName = ({
  value = '',
  onChange,
  disabled,
  error,
  ...props
}: CompanyNameProps) => {
  const [inputValue, businessOrg = BusinessOrgs.gmbh] = makeInputValue(value)

  const handleOrgChange = (org?: string | number) => {
    const orgString = org?.toString()
    onChange(join(' ', [trim(inputValue), orgString]))
  }

  const handleInputChange = (val: string) => {
    if (makeInputValue(val)[1] || !businessOrg) onChange(val)
    else onChange(join(' ', [val, businessOrg]))
  }

  const handleBlur = () => {
    onChange(join(' ', [trim(inputValue), businessOrg]))
  }

  return (
    <GroupInputContainer reversed>
      <Input
        disabled={disabled}
        error={error}
        onBlur={handleBlur}
        onChange={fromEventTarget(handleInputChange)}
        value={inputValue}
        {...props}
      />
      <Select
        autocomplete={false}
        disabled={disabled}
        error={error}
        onSelect={handleOrgChange}
        value={businessOrg}
      >
        {businessOrgs.map((item) => (
          <Option key={item} value={item}>
            {item}
          </Option>
        ))}
      </Select>
    </GroupInputContainer>
  )
}

interface FormCompanyNameProps extends Omit<CompanyNameProps, 'onChange'> {
  name: string
  label?: string
  initialValue?: string
  info?: string
  helperText?: string
  layout?: 'horizontal' | 'vertical'
  noMargin?: boolean
  showErrorHelperText?: boolean
}

export const FormCompanyName = (props: FormCompanyNameProps) => (
  <BasicFormItem component={CompanyName} {...props} />
)
